import {
  Timeline,
  timelineOppositeContentClasses,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";
import { Box, Tooltip, Typography } from "@mui/material";
import { PatientDetailScreenQuery } from "~/graphql/__generated__/graphql";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { useEffect, useMemo } from "react";
import { StatusBgMap } from "~/utils/statuses";
import { formatProvidersNames } from "~/utils/visitsHelper";
import { CalendarPopup } from "../common/calendar/CalendarPopup";
import VisitPopup from "../command/VisitPopup";

type VisitTimelineProps = {
  visits: PatientDetailScreenQuery["visit"];
  onCancel: (visitId: number) => void;
  onEdit: (visitId: number) => void;
  activeVisitId: number | null;
  activeVisitAnchor: HTMLElement | null;
  onOpenVisit: (visitId: number, anchorEl: HTMLElement) => void;
  onCloseVisit: () => void;
};

const VisitTimeline: React.FC<VisitTimelineProps> = ({
  visits,
  onCancel,
  onEdit,
  activeVisitId,
  activeVisitAnchor,
  onOpenVisit,
  onCloseVisit,
}) => {
  const { datef, timeLabelWithZone } = useZonedDateFormat();

  const isActiveVisitInTimeline = useMemo(() => {
    if (!activeVisitId) return false;
    return visits.some((visit) => visit.id === activeVisitId);
  }, [activeVisitId, visits]);

  // Hide popup when user scrolls
  // Reason is because when scrolling while popup open it was causing weird behavior on the popup content
  useEffect(() => {
    if (!isActiveVisitInTimeline) return undefined;

    const handleScroll = () => {
      onCloseVisit();
    };

    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isActiveVisitInTimeline, onCloseVisit]);

  const generateDisplayTime = (visit: { startTime: string, endTime: string }) => {
    return `(${timeLabelWithZone(
      visit.startTime,
      false
    )}
    -
    ${timeLabelWithZone(
      visit.endTime,
      true
    )})`;
  };

  // Group visits by year
  const visitsByYear = visits.reduce((acc, visit) => {
    const visitYear = new Date(visit.startTime).getFullYear();
    if (!acc[visitYear]) {
      acc[visitYear] = [];
    }
    acc[visitYear].push(visit);
    return acc;
  }, {} as Record<number, PatientDetailScreenQuery["visit"]>);

  return (
    <>
      {Object.keys(visitsByYear)
        .sort((a, b) => Number(b) - Number(a)) // Sort years in descending order
        .map((year) => (
          <div key={year}>
            {/* Year heading */}
            <Typography variant="h6" sx={{ marginBottom: 2, marginTop: 0 }}>
              {year}
            </Typography>

            {/* Timeline for the year's visits */}
            <Timeline
              sx={{
                [`& .${timelineOppositeContentClasses.root}`]: {
                  flex: 0.2,
                  paddingLeft: 0,
                },
                "& .MuiTimelineItem-root::before": {
                  flex: "none",
                  padding: 0,
                },
              }}
            >
              {visitsByYear[Number(year)]
                .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()) // Sort visits by startTime in descending order
                .map((visit, index) => {
                  const time = generateDisplayTime(visit);

                  const date = `${datef(visit.startTime, "MMM do")}`;
                  const provider = visit.visitResources[0]?.provider;
                  const providerFullName = formatProvidersNames(visit.visitResources);

                  return (
                    <Box key={visit.id}>
                      <TimelineItem key={visit.id} onClick={(e) => {
                        onOpenVisit(visit.id, e.currentTarget);
                      }} sx={{ 
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                          borderRadius: "8px",
                        }
                      }}>
                        <TimelineSeparator>
                          <Tooltip placement="top" title={visit.eventStatus} arrow>
                            <TimelineDot
                              style={{ backgroundColor: StatusBgMap[visit?.eventStatus] }}
                            />
                          </Tooltip>
                          {visitsByYear[Number(year)].length - 1 !== index && (
                            <TimelineConnector />
                          )}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography variant="body2">
                            <b>{date}</b> {visit.visitType?.name || ''} {provider?.id ? <>with <b>{providerFullName}</b></> : ''} - {time}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                      {activeVisitId === visit.id && activeVisitAnchor && (
                        <CalendarPopup
                          isOpen={true}
                          visitAnchor={activeVisitAnchor}
                          onClose={onCloseVisit}
                        >
                          <VisitPopup
                            visit={visit}
                            onClose={onCloseVisit}
                            onCancel={onCancel}
                            onEdit={onEdit}
                          />
                        </CalendarPopup>
                      )}
                    </Box>
                  );
                })}
            </Timeline>
          </div>
        ))}
    </>
  );
};

export default VisitTimeline;
